
import { AWSModule } from '../types';

export const AWS_ASSOCIATE_MODULES: AWSModule[] = [
  {
    id: 'aws-compute-services-depth',
    title: 'Compute Services in Depth',
    description: 'Deep dive into EC2, Auto Scaling, and Load Balancing for production workloads',
    duration: '75 minutes',
    difficulty: 'intermediate',
    order: 1,
    estimatedTime: '75 minutes',
    prerequisites: ['aws-core-services'],
    learningObjectives: [
      'Configure and launch EC2 instances with advanced options',
      'Implement Auto Scaling for dynamic capacity management',
      'Set up and configure Elastic Load Balancing',
      'Understand EC2 instance families and selection criteria'
    ],
    content: `# Compute Services in Depth

Building on the foundational knowledge of AWS compute services, this module provides an in-depth exploration of Amazon EC2, Auto Scaling, and Load Balancing for production-ready applications.

## Amazon EC2 Deep Dive

### Instance Lifecycle Management

**EC2 Instance States:**
- **Pending**: Instance is being launched
- **Running**: Instance is active and running
- **Stopping**: Instance is being stopped
- **Stopped**: Instance is shut down
- **Shutting-down**: Instance is being terminated
- **Terminated**: Instance is permanently destroyed

**Instance Lifecycle Operations:**
- **Launch**: Create new instance from AMI
- **Start/Stop**: Control instance state (EBS-backed only)
- **Reboot**: Restart operating system
- **Terminate**: Permanently delete instance
- **Hibernation**: Save RAM contents to EBS (supported instances only)

### Advanced Launch Configuration

**Launch Templates vs Launch Configurations:**
- **Launch Templates**: Newer, more flexible, versioned
- **Launch Configurations**: Legacy, immutable
- **Best Practice**: Use Launch Templates for new deployments

### Instance Types and Families

**General Purpose Family:**
- **T3/T4g**: Burstable performance, baseline CPU
- **M5/M6i**: Balanced compute, memory, and networking
- **A1**: ARM-based processors, cost-effective

**Compute Optimized:**
- **C5/C6i**: High-performance processors
- **C6gn**: Network-optimized with Graviton2

**Memory Optimized:**
- **R5/R6i**: Memory-intensive applications
- **X1e/X2gd**: High memory ratios
- **z1d**: High frequency processors + NVMe SSD

**Storage Optimized:**
- **I3/I4i**: NVMe SSD-backed instance storage
- **D2/D3**: Dense HDD storage
- **H1**: High disk throughput

**Accelerated Computing:**
- **P3/P4**: GPU for machine learning
- **G4**: GPU for graphics workstations
- **F1**: Field Programmable Gate Arrays (FPGAs)

### Amazon Machine Images (AMIs)

**AMI Components:**
- **Root Volume Template**: Operating system and applications
- **Launch Permissions**: Who can use the AMI
- **Block Device Mapping**: Storage volumes to attach

**AMI Types:**
- **Amazon Linux 2**: Optimized for AWS
- **Ubuntu**: Popular Linux distribution
- **Windows Server**: Microsoft Windows
- **Red Hat Enterprise Linux**: Enterprise-grade Linux
- **Custom AMIs**: Your own configured images

**Creating Custom AMIs:**
1. Launch base instance
2. Install and configure software
3. Create AMI from instance
4. Test new AMI launches
5. Share or make public if needed

## Auto Scaling

### Auto Scaling Groups (ASGs)

**What is Auto Scaling?**
- Automatically adjust the number of EC2 instances
- Maintain desired capacity based on demand
- Respond to health checks and metrics
- Integrate with load balancers

**ASG Components:**
- **Launch Template/Configuration**: Instance specification
- **Minimum Size**: Smallest number of instances
- **Maximum Size**: Largest number of instances
- **Desired Capacity**: Target number of instances
- **Availability Zones**: Where to launch instances

### Scaling Policies

**Target Tracking Scaling:**
- Maintain specific metric target (e.g., 50% CPU utilization)
- AWS automatically adjusts capacity
- Most common and recommended approach

**Step Scaling:**
- Scale based on CloudWatch alarm severity
- Define multiple scaling steps
- More control over scaling behavior

**Simple Scaling:**
- Add or remove fixed number of instances
- Based on single CloudWatch alarm
- Less flexible than other options

**Scheduled Scaling:**
- Scale based on predictable patterns
- Useful for known traffic patterns
- Can be recurring or one-time

### Health Checks

**EC2 Health Checks:**
- Basic instance health monitoring
- Checks instance status and system status
- Replaces failed instances automatically

**ELB Health Checks:**
- Application-level health monitoring
- More sophisticated than EC2 health checks
- Considers application responsiveness

**Custom Health Checks:**
- Use external monitoring systems
- Set instance health programmatically
- Most flexible option

## Elastic Load Balancing

### Load Balancer Types

**Application Load Balancer (ALB):**
- **Layer 7**: HTTP/HTTPS traffic
- **Advanced Routing**: Path, host, header-based routing
- **Target Types**: EC2 instances, IP addresses, Lambda functions
- **Features**: SSL termination, WebSocket support, HTTP/2

**Network Load Balancer (NLB):**
- **Layer 4**: TCP, UDP, TLS traffic
- **High Performance**: Millions of requests per second
- **Static IP**: One static IP per AZ
- **Features**: Ultra-low latency, preserve source IP

**Gateway Load Balancer (GWLB):**
- **Layer 3**: Deploy and scale third-party appliances
- **Use Cases**: Firewalls, intrusion detection, deep packet inspection
- **Features**: Transparent network gateway, load balancing

**Classic Load Balancer (CLB):**
- **Legacy**: Previous generation
- **Layer 4 and 7**: Basic load balancing
- **Recommendation**: Migrate to ALB or NLB

### ALB Advanced Features

**Target Groups:**
- Group of targets (instances, IPs, Lambda functions)
- Health checks and routing rules
- Multiple target groups per load balancer
- Different target types in same group

**Listener Rules:**
- Define how requests are routed
- Conditions: host header, path, HTTP method, query string
- Actions: forward, redirect, return fixed response

**Sticky Sessions (Session Affinity):**
- Route requests from same client to same target
- Application-controlled or load balancer-generated cookies
- Duration-based or application-controlled duration

**SSL/TLS Termination:**
- Terminate SSL at load balancer
- Reduce CPU load on backend instances
- Centralized certificate management
- Support for multiple certificates (SNI)

### Health Checks and Monitoring

**Health Check Configuration:**
- **Protocol**: HTTP, HTTPS, TCP
- **Port**: Target port for health checks
- **Path**: Health check endpoint (HTTP/HTTPS)
- **Interval**: Time between health checks
- **Timeout**: Time to wait for response
- **Healthy/Unhealthy Threshold**: Number of checks to change status

**CloudWatch Metrics:**
- Request count and latency
- Error rates (4xx, 5xx)
- Target health
- Active connections

**Access Logs:**
- Detailed information about requests
- Store in S3 for analysis
- Include client IP, latencies, request paths
- Useful for troubleshooting and analytics

## Integration Patterns

### Three-Tier Architecture

**Web Tier (Public Subnets):**
- Application Load Balancer
- Auto Scaling Group with web servers
- Security groups allowing HTTP/HTTPS

**Application Tier (Private Subnets):**
- Auto Scaling Group with application servers
- Internal load balancer (optional)
- Security groups allowing traffic from web tier

**Database Tier (Private Subnets):**
- RDS Multi-AZ deployment
- Security groups allowing traffic from app tier
- Database subnet group

### Microservices Architecture

**Service Mesh with ALB:**
- ALB routes to different services based on path
- Each service has its own target group
- Independent scaling for each service
- Service discovery integration

**Container-based Deployment:**
- ECS/EKS clusters in Auto Scaling Groups
- Service auto-scaling based on metrics
- Application Load Balancer integration
- Blue/green deployments

## Summary

Advanced compute services enable scalable, highly available applications:

- **EC2 Deep Dive**: Instance families, AMIs, storage, security
- **Auto Scaling**: Dynamic capacity management with policies
- **Load Balancing**: Distribute traffic with health checks
- **Integration**: Combine services for robust architectures
- **Monitoring**: Track performance and troubleshoot issues

These services form the foundation for modern, cloud-native applications that can handle varying loads while maintaining performance and availability.`,
    quiz: {
      questions: [
        {
          id: 'q1',
          question: 'Which Auto Scaling policy type automatically maintains a target metric value?',
          options: [
            'Simple Scaling',
            'Step Scaling',
            'Target Tracking Scaling',
            'Scheduled Scaling'
          ],
          correctAnswer: 2
        },
        {
          id: 'q2',
          question: 'What is the main difference between Security Groups and NACLs?',
          options: [
            'Security Groups are stateful, NACLs are stateless',
            'Security Groups are for subnets, NACLs are for instances',
            'Security Groups support deny rules, NACLs do not',
            'Security Groups are regional, NACLs are global'
          ],
          correctAnswer: 0
        },
        {
          id: 'q3',
          question: 'Which load balancer type operates at Layer 7?',
          options: [
            'Classic Load Balancer',
            'Network Load Balancer',
            'Application Load Balancer',
            'Gateway Load Balancer'
          ],
          correctAnswer: 2
        },
        {
          id: 'q4',
          question: 'What is the benefit of using Launch Templates over Launch Configurations?',
          options: [
            'Launch Templates are cheaper',
            'Launch Templates support versioning and are more flexible',
            'Launch Templates work with more instance types',
            'Launch Templates have better security'
          ],
          correctAnswer: 1
        }
      ]
    }
  },
  {
    id: 'aws-storage-database-services',
    title: 'Storage and Database Services',
    description: 'Advanced storage and database solutions for enterprise applications',
    duration: '80 minutes',
    difficulty: 'intermediate',
    order: 2,
    estimatedTime: '80 minutes',
    prerequisites: ['aws-core-services'],
    learningObjectives: [
      'Configure advanced S3 features and lifecycle policies',
      'Implement RDS Multi-AZ and Read Replicas',
      'Design DynamoDB tables with proper partition strategies',
      'Choose appropriate storage solutions for different use cases'
    ],
    content: `# Storage and Database Services

This module provides comprehensive coverage of AWS storage and database services, focusing on advanced configurations, performance optimization, and architectural patterns for enterprise applications.

## Amazon S3 Advanced Features

### S3 Storage Classes Deep Dive

**S3 Standard:**
- **Use Case**: Frequently accessed data
- **Availability**: 99.99%
- **Durability**: 99.999999999% (11 9's)
- **Minimum Storage Duration**: None
- **Retrieval Fee**: None

**S3 Standard-Infrequent Access (Standard-IA):**
- **Use Case**: Long-lived, infrequently accessed data
- **Availability**: 99.9%
- **Cost**: Lower storage cost, retrieval fees apply
- **Minimum Storage Duration**: 30 days

**S3 One Zone-Infrequent Access:**
- **Use Case**: Non-critical, infrequently accessed data
- **Availability**: 99.5% (single AZ)
- **Cost**: 20% less than Standard-IA
- **Risk**: Data lost if AZ is destroyed

**S3 Intelligent-Tiering:**
- **Use Case**: Unknown or changing access patterns
- **Automation**: Moves objects between tiers automatically
- **Cost**: Small monitoring fee per object
- **Benefit**: Optimizes costs without performance impact

**S3 Glacier:**
- **Use Case**: Archive data, backup
- **Retrieval Time**: 1-5 minutes to 12 hours
- **Cost**: Very low storage cost
- **Durability**: 99.999999999% (11 9's)

**S3 Glacier Deep Archive:**
- **Use Case**: Long-term archive, compliance
- **Retrieval Time**: 12-48 hours
- **Cost**: Lowest storage cost
- **Minimum Storage Duration**: 180 days

### S3 Lifecycle Management

**Lifecycle Policies:**
Automatically transition objects between storage classes or delete them based on age and access patterns.

**Transition Actions:**
- Move objects to different storage classes
- Based on object age or last access time
- Reduce storage costs automatically

**Expiration Actions:**
- Delete objects after specified time
- Delete incomplete multipart uploads
- Clean up old versions in versioned buckets

### S3 Security Features

**Bucket Policies:**
Resource-based policies that define permissions for entire buckets and objects within them.

**Access Control Lists (ACLs):**
Legacy access control mechanism, use IAM policies instead when possible.

**Encryption:**
- **Server-Side Encryption (SSE-S3)**: AWS managed keys
- **SSE-KMS**: AWS KMS managed keys
- **SSE-C**: Customer-provided keys
- **Client-Side Encryption**: Encrypt before upload

**Cross-Origin Resource Sharing (CORS):**
Enable web browsers to access S3 resources from different domains.

**Pre-signed URLs:**
Provide temporary access to specific S3 objects without AWS credentials.

### S3 Performance Optimization

**Request Rate Performance:**
- 3,500 PUT/COPY/POST/DELETE requests per second per prefix
- 5,500 GET/HEAD requests per second per prefix
- Use random prefixes to distribute load

**Transfer Acceleration:**
- Use CloudFront edge locations to upload to S3
- Faster uploads from distant locations
- Additional cost but improved performance

**Multipart Upload:**
- Required for objects larger than 5GB
- Recommended for objects larger than 100MB
- Parallel uploads improve performance
- Resume failed uploads

## Amazon RDS Deep Dive

### RDS Engine Options

**MySQL:**
- **Version**: 5.7, 8.0
- **Use Cases**: Web applications, e-commerce
- **Features**: InnoDB storage engine, point-in-time recovery

**PostgreSQL:**
- **Version**: 11, 12, 13, 14
- **Use Cases**: Complex queries, ACID compliance
- **Features**: Advanced indexing, full-text search

**Oracle:**
- **Editions**: Standard Edition, Enterprise Edition
- **Licensing**: Bring Your Own License (BYOL) or License Included
- **Features**: Advanced security, partitioning

**SQL Server:**
- **Editions**: Express, Web, Standard, Enterprise
- **Features**: Integration Services, Analysis Services
- **Windows Authentication**: Active Directory integration

**MariaDB:**
- **MySQL Compatible**: Drop-in replacement for MySQL
- **Features**: Columnar storage, temporal tables

**Amazon Aurora:**
- **MySQL and PostgreSQL Compatible**: Up to 5x MySQL, 3x PostgreSQL performance
- **Cloud-Native**: Built for the cloud from ground up
- **Features**: Auto-scaling storage, serverless option

### Multi-AZ Deployments

**How Multi-AZ Works:**
- Synchronous replication to standby instance
- Automatic failover in case of failure
- No performance benefit for reads
- Increased availability and data durability

**Failover Scenarios:**
- Primary database instance failure
- Availability Zone outage
- Database instance class modification
- Operating system patching

**Failover Process:**
1. RDS detects failure
2. Updates DNS record to point to standby
3. Application automatically connects to new primary
4. Typical failover time: 1-2 minutes

### Read Replicas

**Read Replica Benefits:**
- Offload read traffic from primary database
- Scale read capacity beyond single database
- Cross-region replicas for disaster recovery
- Promote replica to standalone database

**Read Replica Limitations:**
- Asynchronous replication (eventual consistency)
- MySQL, PostgreSQL, MariaDB, Oracle, SQL Server supported
- Maximum 5 read replicas per primary
- Cannot write to read replicas

**Cross-Region Read Replicas:**
- Disaster recovery in different AWS region
- Global application with local read access
- Migration assistance for database move

### RDS Security

**Network Security:**
- Deploy in VPC private subnets
- Security groups control access
- SSL/TLS encryption in transit

**Access Control:**
- IAM database authentication
- Database user management
- Resource-level permissions

**Encryption:**
- Encryption at rest using AWS KMS
- Automated backups, snapshots, read replicas encrypted
- Cannot encrypt existing unencrypted database

**Monitoring and Auditing:**
- CloudWatch metrics and logs
- Performance Insights for query analysis
- Database activity streams for compliance

## Amazon DynamoDB

### DynamoDB Core Concepts

**Tables:**
- Collection of items (records)
- Schema-less (except for primary key)
- Automatically partitioned across multiple servers

**Items:**
- Individual records in a table
- Maximum size: 400KB
- Composed of attributes

**Attributes:**
- Name-value pairs
- Scalar, document, or set data types
- Nested attributes supported

### Primary Keys

**Partition Key (Hash Key):**
- Single attribute primary key
- DynamoDB uses partition key value as input to hash function
- Output determines partition where item is stored

**Composite Primary Key:**
- Partition key + sort key (range key)
- Partition key determines partition
- Sort key determines order within partition
- Combination must be unique

### DynamoDB Performance

**Read Consistency:**
- **Eventually Consistent Reads**: Default, may not reflect recent write
- **Strongly Consistent Reads**: Returns most up-to-date data
- Strong consistency costs twice the read capacity units

**Capacity Modes:**
- **Provisioned**: Specify read/write capacity units
- **On-Demand**: Pay per request, automatic scaling

**Hot Partitions:**
- Uneven access patterns cause performance issues
- Use compound attributes to distribute load
- Add random suffix to partition key if needed

### DynamoDB Global Tables

**Multi-Region Replication:**
- Fully managed multi-master replication
- Automatic conflict resolution
- Eventually consistent across regions

**Use Cases:**
- Global applications requiring low latency
- Disaster recovery across regions
- Compliance with data residency requirements

### DynamoDB Streams

**Change Data Capture:**
- Record changes to items in DynamoDB table
- Trigger Lambda functions on data changes
- 24-hour retention of change records

**Stream Record Information:**
- **KEYS_ONLY**: Only key attributes of modified item
- **NEW_IMAGE**: Entire item after modification
- **OLD_IMAGE**: Entire item before modification
- **NEW_AND_OLD_IMAGES**: Both new and old images

## Database Selection Criteria

### Relational vs NoSQL

**Choose RDS When:**
- ACID transactions required
- Complex queries and joins
- Existing application uses SQL
- Strong consistency requirements

**Choose DynamoDB When:**
- Massive scale requirements
- Predictable access patterns
- Fast, consistent performance needed
- Serverless application architecture

### Performance Considerations

**RDS Performance Factors:**
- Instance class (CPU, memory)
- Storage type (gp2, gp3, io1, io2)
- Multi-AZ deployment overhead
- Read replica distribution

**DynamoDB Performance Factors:**
- Partition key design
- Access patterns and hot keys
- Item size and attribute count
- Global secondary index usage

### Cost Optimization

**RDS Cost Optimization:**
- Right-size instances based on utilization
- Use Reserved Instances for steady workloads
- Schedule start/stop for development environments
- Optimize storage based on access patterns

**DynamoDB Cost Optimization:**
- Choose appropriate capacity mode
- Use Global Secondary Indexes judiciously
- Implement data archiving strategies
- Monitor and optimize partition key distribution

## Backup and Recovery Strategies

### RDS Backup Options

**Automated Backups:**
- Point-in-time recovery within retention period
- 1-35 day retention (default 7 days)
- Stored in S3, no additional charge
- Backup window can be specified

**Manual Snapshots:**
- User-initiated backups
- Retained until manually deleted
- Can be shared across AWS accounts
- Copy snapshots across regions

### DynamoDB Backup Options

**On-Demand Backups:**
- Full backup of table data and settings
- Retained until manually deleted
- No performance impact
- Restore to new table only

**Point-in-Time Recovery (PITR):**
- Continuous backups for 35 days
- Restore to any point within retention period
- Restore to new table only
- Additional cost applies

### Disaster Recovery Planning

**RTO and RPO Objectives:**
- **Recovery Time Objective**: Maximum acceptable downtime
- **Recovery Point Objective**: Maximum acceptable data loss

**Multi-Region Strategy:**
- Cross-region read replicas for RDS
- DynamoDB Global Tables for multi-region
- Regular testing of failover procedures

## Summary

AWS storage and database services provide scalable, secure, and high-performance solutions:

- **S3**: Object storage with multiple classes and lifecycle management
- **RDS**: Managed relational databases with Multi-AZ and read replicas
- **DynamoDB**: NoSQL database with global scale and single-digit millisecond latency
- **Security**: Encryption, access control, and monitoring
- **Performance**: Optimization strategies for different workloads
- **Backup**: Multiple backup and recovery options

Choose the right service based on your application requirements, consistency needs, and scale requirements.`,
    quiz: {
      questions: [
        {
          id: 'q1',
          question: 'Which S3 storage class automatically moves objects between access tiers?',
          options: [
            'S3 Standard',
            'S3 Standard-IA',
            'S3 Intelligent-Tiering',
            'S3 Glacier'
          ],
          correctAnswer: 2
        },
        {
          id: 'q2',
          question: 'What is the main benefit of RDS Multi-AZ deployment?',
          options: [
            'Improved read performance',
            'High availability and automatic failover',
            'Lower costs',
            'Better security'
          ],
          correctAnswer: 1
        },
        {
          id: 'q3',
          question: 'What is the maximum item size in DynamoDB?',
          options: [
            '64KB',
            '256KB',
            '400KB',
            '1MB'
          ],
          correctAnswer: 2
        },
        {
          id: 'q4',
          question: 'Which consistency model does DynamoDB use by default?',
          options: [
            'Strong consistency',
            'Eventual consistency',
            'Immediate consistency',
            'Transactional consistency'
          ],
          correctAnswer: 1
        }
      ]
    }
  },
  {
    id: 'aws-networking',
    title: 'Networking in AWS',
    description: 'Design and implement secure, scalable network architectures in AWS',
    duration: '70 minutes',
    difficulty: 'intermediate',
    order: 3,
    estimatedTime: '70 minutes',
    prerequisites: ['aws-core-services'],
    learningObjectives: [
      'Design VPC architectures with proper subnet strategies',
      'Configure routing, NAT, and internet connectivity',
      'Implement network security with Security Groups and NACLs',
      'Set up VPN and Direct Connect for hybrid connectivity'
    ],
    content: `# Networking in AWS

This module covers advanced networking concepts in AWS, focusing on Virtual Private Cloud (VPC) design, routing, security, and hybrid connectivity patterns for enterprise applications.

## VPC Fundamentals

### What is a VPC?

A Virtual Private Cloud (VPC) is a logically isolated section of the AWS cloud where you can launch AWS resources in a virtual network that you define.

**Key Benefits:**
- Complete control over network environment
- Choose IP address ranges, subnets, route tables
- Configure network gateways and security settings
- Isolate resources for security and compliance

### VPC Components

**CIDR Block:**
- Defines IP address range for VPC
- Must be between /16 and /28
- Cannot be changed after VPC creation
- Example: 10.0.0.0/16 provides 65,536 IP addresses

**Subnets:**
- Logical subdivision of VPC
- Must be within VPC CIDR block
- Associated with single Availability Zone
- Public or private based on routing configuration

**Route Tables:**
- Control traffic flow between subnets
- Each subnet associated with one route table
- Default route table created with VPC
- Custom route tables for specific routing needs

**Internet Gateway (IGW):**
- Provides internet access to VPC
- Horizontally scaled, redundant, highly available
- One IGW per VPC
- Required for public subnet internet access

**NAT Gateway/Instance:**
- Enables outbound internet access for private subnets
- NAT Gateway: Managed service, highly available
- NAT Instance: EC2 instance, more control but less reliable

## Subnet Design Strategies

### Public vs Private Subnets

**Public Subnets:**
- Direct route to Internet Gateway
- Resources get public IP addresses
- Used for web servers, load balancers, bastion hosts
- Security groups and NACLs for protection

**Private Subnets:**
- No direct internet access
- Use NAT Gateway/Instance for outbound internet
- Used for application servers, databases
- Enhanced security through isolation

### Multi-Tier Architecture

**Web Tier (Public Subnets):**
- Application Load Balancer
- Web servers (if needed)
- Bastion hosts for administrative access

**Application Tier (Private Subnets):**
- Application servers
- Internal load balancers
- Auto Scaling Groups

**Database Tier (Private Subnets):**
- RDS instances
- ElastiCache clusters
- Database security groups

### Availability Zone Strategy

**Multi-AZ Deployment:**
- Deploy subnets across multiple AZs
- Ensures high availability
- Required for load balancers and RDS Multi-AZ

**Subnet Naming Convention:**
- Include environment, tier, and AZ
- Example: prod-web-us-east-1a, prod-app-us-east-1b

## Routing and Connectivity

### Route Tables Deep Dive

**Default Route Table:**
- Automatically created with VPC
- Contains local route for VPC CIDR
- Can be modified but better to create custom tables

**Custom Route Tables:**
- Created for specific routing requirements
- Associate with subnets as needed
- More granular control over traffic flow

**Route Priority:**
- Most specific route takes precedence
- Local routes always take priority
- Cannot override local routes

### Internet Connectivity Options

**Internet Gateway Routing:**
- Route 0.0.0.0/0 to Internet Gateway
- Required for public subnet functionality
- Automatic route for subnet's local traffic

**NAT Gateway Configuration:**
- Deploy in public subnet
- Route private subnet traffic (0.0.0.0/0) to NAT Gateway
- Provides outbound internet access only

**NAT Instance vs NAT Gateway:**
- NAT Gateway: Managed, highly available, no security groups
- NAT Instance: Self-managed, can use security groups, supports port forwarding

### VPC Peering

**What is VPC Peering?**
- Private connectivity between VPCs
- Same region or cross-region
- No single point of failure
- No bandwidth bottleneck

**VPC Peering Limitations:**
- No transitive peering
- No overlapping CIDR blocks
- DNS resolution must be enabled on both sides
- Security groups cannot reference peered VPC groups

**Peering Best Practices:**
- Plan CIDR blocks to avoid overlap
- Use descriptive names for peering connections
- Document routing requirements
- Test connectivity before production use

## Advanced Networking Features

### Elastic Network Interfaces (ENIs)

**What are ENIs?**
- Virtual network interfaces
- Can be attached/detached from instances
- Retain attributes when moved
- Support multiple private IP addresses

**ENI Use Cases:**
- Management networks
- Network and security appliances
- Dual-homed instances
- Low-budget high-availability solutions

### Enhanced Networking

**SR-IOV:**
- Single Root I/O Virtualization
- Higher bandwidth, lower latency
- Supported on most current instance types
- Enabled by default on newer instances

**Elastic Network Adapter (ENA):**
- Custom network interface for high performance
- Up to 100 Gbps network performance
- Lower latency than traditional interfaces
- Required for some instance types

### Placement Groups

**Cluster Placement Groups:**
- Pack instances close together in single AZ
- Low latency, high network throughput
- Use for HPC applications
- Limited to specific instance types

**Partition Placement Groups:**
- Spread instances across logical partitions
- Each partition on separate hardware racks
- Reduce correlated hardware failures
- Use for distributed workloads

**Spread Placement Groups:**
- Spread instances across underlying hardware
- Maximum 7 instances per AZ
- Reduce risk of simultaneous failures
- Use for critical applications

## Network Security

### Security Groups

**Security Group Characteristics:**
- Virtual firewall for instances
- Stateful: return traffic automatically allowed
- Allow rules only (no deny rules)
- Evaluate all rules before allowing traffic

**Security Group Best Practices:**
- Use descriptive names and descriptions
- Follow principle of least privilege
- Reference other security groups when possible
- Regular audit and cleanup of unused rules

**Common Security Group Patterns:**
- Web tier: Allow HTTP/HTTPS from anywhere
- App tier: Allow traffic from web tier only
- Database tier: Allow database ports from app tier only
- Management: Allow SSH/RDP from specific IPs

### Network Access Control Lists (NACLs)

**NACL Characteristics:**
- Subnet-level firewall
- Stateless: return traffic must be explicitly allowed
- Support both allow and deny rules
- Process rules in order by rule number

**When to Use NACLs:**
- Additional layer of security
- Deny specific IP addresses or ranges
- Compliance requirements
- Defense in depth strategy

**NACL Best Practices:**
- Start with default NACL (allows all traffic)
- Use incrementally lower rule numbers for important rules
- Consider ephemeral port ranges for outbound responses
- Test thoroughly when implementing custom NACLs

### AWS Web Application Firewall (WAF)

**WAF Capabilities:**
- Application layer protection
- SQL injection and XSS protection
- Rate limiting and IP reputation
- Geographic restrictions

**WAF Integration:**
- Application Load Balancer
- CloudFront distributions
- API Gateway
- AWS AppSync

## Hybrid Connectivity

### AWS Site-to-Site VPN

**VPN Components:**
- Customer Gateway: Customer side endpoint
- Virtual Private Gateway: AWS side endpoint
- VPN Connection: Encrypted tunnel between gateways

**VPN Routing:**
- Static routing: Manual route configuration
- Dynamic routing: BGP for automatic route propagation
- Route propagation to route tables

**VPN Redundancy:**
- Two tunnels per connection for high availability
- Multiple VPN connections for additional redundancy
- Consider Direct Connect backup

### AWS Direct Connect

**What is Direct Connect?**
- Dedicated network connection to AWS
- Consistent network performance
- Reduce bandwidth costs
- Private connectivity options

**Direct Connect Components:**
- **Direct Connect Location**: AWS facility with networking equipment
- **Cross Connect**: Physical connection to your router
- **Virtual Interfaces (VIFs)**: VLAN tags for different connections

**VIF Types:**
- **Private VIF**: Access VPC via Virtual Private Gateway
- **Public VIF**: Access AWS public services
- **Transit VIF**: Access multiple VPCs via Transit Gateway

### AWS Transit Gateway

**What is Transit Gateway?**
- Central hub for connecting VPCs and on-premises networks
- Simplifies complex network topologies
- Supports thousands of VPCs
- Regional service with cross-region peering

**Transit Gateway Benefits:**
- Reduce complexity of network connections
- Centralized routing and security policies
- Scale VPC connectivity
- Multi-account networking

**Transit Gateway Attachments:**
- VPCs in same region
- VPN connections
- Direct Connect gateways
- Other Transit Gateways (peering)

## DNS and Service Discovery

### Amazon Route 53

**Route 53 Features:**
- Authoritative DNS service
- Domain registration
- Health checking and failover
- Traffic routing policies

**Routing Policies:**
- **Simple**: Single resource record
- **Weighted**: Distribute traffic across multiple resources
- **Latency-based**: Route to lowest latency endpoint
- **Failover**: Primary/secondary failover configuration
- **Geolocation**: Route based on user location
- **Multivalue**: Return multiple healthy endpoints

### Private DNS

**Route 53 Private Hosted Zones:**
- DNS resolution within VPC
- Override public DNS records
- Cross-account VPC associations
- Hybrid DNS with on-premises

**DNS Resolution in VPC:**
- Enable DNS resolution and DNS hostnames
- AmazonProvidedDNS at 169.254.169.253
- Custom DNS servers if needed

## Network Monitoring and Troubleshooting

### VPC Flow Logs

**What are Flow Logs?**
- Capture network traffic metadata
- Available at VPC, subnet, or ENI level
- Store in CloudWatch Logs or S3
- No performance impact on network traffic

**Flow Log Information:**
- Source and destination IP addresses
- Source and destination ports
- Protocol and packet count
- Accept/reject actions

**Flow Log Use Cases:**
- Network monitoring and troubleshooting
- Security analysis and forensics
- Cost optimization
- Compliance auditing

### Network Troubleshooting

**Common Connectivity Issues:**
- Incorrect security group rules
- Missing route table entries
- NACL blocking traffic
- DNS resolution problems

**Troubleshooting Tools:**
- VPC Flow Logs for traffic analysis
- VPC Reachability Analyzer for path analysis
- CloudWatch metrics for network performance
- AWS X-Ray for application-level tracing

**Network Performance Optimization:**
- Choose appropriate instance types
- Use placement groups for high-performance computing
- Enable enhanced networking
- Optimize security group rules

## Best Practices and Design Patterns

### VPC Design Best Practices

**CIDR Planning:**
- Use RFC 1918 private address space
- Plan for future growth
- Avoid overlapping ranges
- Document IP allocation strategy

**Security Best Practices:**
- Implement defense in depth
- Use least privilege access
- Regular security group audits
- Enable VPC Flow Logs
- Use AWS Config for compliance

**High Availability Design:**
- Multi-AZ deployment
- Redundant network paths
- Load balancer health checks
- Auto Scaling for capacity

### Cost Optimization

**Data Transfer Costs:**
- Same AZ transfers are free
- Cross-AZ transfers incur charges
- Internet data transfer costs
- Use CloudFront for global content delivery

**NAT Gateway Optimization:**
- Right-size NAT Gateways
- Consider NAT instances for specific use cases
- Monitor data processing charges
- Use VPC endpoints to avoid NAT charges

## Summary

AWS networking provides comprehensive tools for building secure, scalable network architectures:

- **VPC Design**: Plan subnets, routing, and connectivity
- **Security**: Implement multiple layers of network security
- **Hybrid Connectivity**: Connect on-premises to AWS
- **Performance**: Optimize for throughput and latency
- **Monitoring**: Use Flow Logs and CloudWatch for visibility
- **Cost Management**: Understand data transfer charges

Proper network design is fundamental to successful AWS implementations, providing the foundation for secure, performant, and cost-effective cloud architectures.`,
    quiz: {
      questions: [
        {
          id: 'q1',
          question: 'What is the maximum CIDR block size for a VPC?',
          options: [
            '/8',
            '/16',
            '/24',
            '/28'
          ],
          correctAnswer: 1
        },
        {
          id: 'q2',
          question: 'Which AWS service provides dedicated network connectivity to AWS?',
          options: [
            'VPN Gateway',
            'Internet Gateway',
            'Direct Connect',
            'NAT Gateway'
          ],
          correctAnswer: 2
        },
        {
          id: 'q3',
          question: 'What is the main difference between Security Groups and NACLs?',
          options: [
            'Security Groups are stateful, NACLs are stateless',
            'Security Groups operate at subnet level, NACLs at instance level',
            'Security Groups support deny rules, NACLs do not',
            'Security Groups are regional, NACLs are global'
          ],
          correctAnswer: 0
        },
        {
          id: 'q4',
          question: 'Which service simplifies connecting multiple VPCs and on-premises networks?',
          options: [
            'VPC Peering',
            'Transit Gateway',
            'Internet Gateway',
            'Virtual Private Gateway'
          ],
          correctAnswer: 1
        }
      ]
    }
  },
  {
    id: 'aws-monitoring-management',
    title: 'Monitoring and Management Tools',
    description: 'Implement comprehensive monitoring, logging, and management strategies',
    duration: '65 minutes',
    difficulty: 'intermediate',
    order: 4,
    estimatedTime: '65 minutes',
    prerequisites: ['aws-core-services'],
    learningObjectives: [
      'Configure CloudWatch metrics, logs, and alarms',
      'Implement distributed tracing with X-Ray',
      'Set up compliance monitoring with AWS Config',
      'Design automated response systems with EventBridge'
    ],
    content: `# Monitoring and Management Tools

This module covers AWS monitoring and management services essential for maintaining operational excellence, ensuring compliance, and automating responses to system events.

## Amazon CloudWatch

### CloudWatch Metrics

**What are Metrics?**
- Time-ordered set of data points
- Represent system and application performance
- Automatically collected for AWS services
- Custom metrics for applications

**Default Metrics by Service:**
- **EC2**: CPU utilization, network I/O, disk I/O
- **RDS**: Database connections, CPU utilization, read/write IOPS
- **S3**: Bucket size, number of objects, request count
- **Lambda**: Invocations, duration, errors, throttles

**Custom Metrics:**
- Application-specific performance indicators
- Business metrics and KPIs
- Published via AWS SDK or CLI
- High-resolution metrics (1-second granularity)

**Metric Dimensions:**
- Name-value pairs that uniquely identify metrics
- Filter and aggregate metrics
- Example: InstanceId, AutoScalingGroupName

### CloudWatch Logs

**Log Groups and Streams:**
- **Log Group**: Collection of log streams with same retention and access control
- **Log Stream**: Sequence of log events from same source
- **Log Events**: Individual log entries with timestamp and message

**Log Sources:**
- EC2 instances via CloudWatch agent
- Lambda functions automatically
- ECS containers
- API Gateway
- CloudTrail

**Log Insights:**
- Interactive query and analysis
- Purpose-built query language
- Visualize query results
- Automated discovery of fields

### CloudWatch Alarms

**Alarm States:**
- **OK**: Metric within threshold
- **ALARM**: Metric breached threshold
- **INSUFFICIENT_DATA**: Not enough data to determine state

**Alarm Actions:**
- **SNS Notification**: Send notifications via email, SMS, HTTP
- **Auto Scaling**: Trigger scaling policies
- **EC2 Actions**: Stop, terminate, reboot, recover instances

**Alarm Best Practices:**
- Set meaningful alarm names and descriptions
- Use appropriate evaluation periods and data points
- Avoid alarm fatigue with proper thresholds
- Test alarm actions before production deployment

### CloudWatch Dashboards

**Dashboard Components:**
- **Metrics Widgets**: Display metric data over time
- **Log Widgets**: Show log data and queries
- **Text Widgets**: Add context and documentation
- **Custom Widgets**: Third-party integrations

**Dashboard Sharing:**
- Share publicly or with specific AWS accounts
- Embed in web applications
- Cross-region and cross-account access
- Mobile-friendly responsive design

## AWS X-Ray

### Distributed Tracing Concepts

**What is Distributed Tracing?**
- Track requests across multiple services
- Identify performance bottlenecks
- Understand service dependencies
- Debug complex microservices architectures

**X-Ray Components:**
- **Traces**: End-to-end request journey
- **Segments**: Individual service operations
- **Subsegments**: Granular operations within segments
- **Service Map**: Visual representation of application architecture

### X-Ray Implementation

**Instrumentation:**
- Add X-Ray SDK to applications
- Automatic instrumentation for AWS services
- Custom segments for specific operations
- Sampling rules to control trace volume

**X-Ray Daemon:**
- Local process that relays trace data
- Runs on EC2, ECS, Lambda automatically
- Buffers and sends traces to X-Ray service
- Minimal performance overhead

**Service Integration:**
- **Lambda**: Enabled with simple toggle
- **API Gateway**: Trace API requests
- **ELB**: Trace load balancer requests
- **ECS/EKS**: Container-level tracing

### X-Ray Analysis

**Service Map:**
- Visual representation of service relationships
- Response time and error rate for each service
- Identify problematic services quickly
- Understand traffic patterns

**Trace Analysis:**
- Drill down into specific requests
- Identify slow database queries
- Find error root causes
- Performance optimization opportunities

**Insights:**
- Automatic detection of anomalies
- Root cause analysis suggestions
- Performance recommendations
- Impact assessment

## AWS CloudTrail

### API Activity Logging

**What does CloudTrail Log?**
- API calls made to AWS services
- User identity and source IP
- Request parameters and responses
- Timestamp and user agent information

**Event Types:**
- **Management Events**: Control plane operations (create, delete, modify)
- **Data Events**: Data plane operations (S3 object access, Lambda invocations)
- **Insight Events**: Unusual activity patterns

### CloudTrail Configuration

**Trail Setup:**
- **Single Region**: Events in one region only
- **All Regions**: Current and future regions
- **Organization Trail**: All accounts in AWS Organizations

**Log File Delivery:**
- Store in S3 bucket
- Optional encryption with KMS
- Log file validation for integrity
- Real-time delivery with CloudWatch Logs

**Advanced Features:**
- **Data Events**: Detailed S3 and Lambda logging
- **Insights**: Machine learning-powered anomaly detection
- **Event History**: 90 days of management events (no trail required)

### CloudTrail Analysis

**CloudTrail Insights:**
- Detect unusual operational patterns
- Identify spikes in API activity
- Root cause analysis assistance
- Baseline normal activity levels

**Integration with Other Services:**
- **CloudWatch**: Create metrics from log data
- **Amazon Athena**: Query logs with SQL
- **AWS Config**: Compliance monitoring
- **Security Hub**: Centralized security findings

## AWS Config

### Configuration Management

**What is AWS Config?**
- Track resource configurations over time
- Monitor compliance with organizational standards
- Automated remediation of non-compliant resources
- Change history and relationship tracking

**Configuration Items:**
- Point-in-time configuration of AWS resources
- Metadata, attributes, relationships, events
- Timeline of configuration changes
- Resource relationship mapping

### Config Rules

**Rule Types:**
- **AWS Managed Rules**: Pre-built compliance checks
- **Custom Rules**: Lambda-based custom logic
- **Organization Rules**: Apply across AWS Organizations

**Common Rule Examples:**
- Ensure S3 buckets are not publicly readable
- Verify security groups don't allow unrestricted access
- Check for encrypted EBS volumes
- Validate IAM password policy compliance

**Remediation Actions:**
- **Manual**: Notification of non-compliance
- **Automatic**: Lambda function fixes issues
- **AWS Systems Manager**: Run automation documents

### Compliance Monitoring

**Compliance Dashboard:**
- Overall compliance score
- Resource compliance status
- Historical compliance trends
- Non-compliant resource identification

**Compliance Packs:**
- Collection of Config rules and remediation actions
- Industry standards (PCI DSS, HIPAA, SOC)
- Organizational security baselines
- Operational best practices

## Amazon EventBridge

### Event-Driven Architecture

**What is EventBridge?**
- Serverless event bus service
- Connect applications with event data
- Route events to targets based on rules
- Decouple event producers and consumers

**Event Sources:**
- **AWS Services**: CloudWatch, CodeCommit, EC2
- **SaaS Partners**: Shopify, Zendesk, PagerDuty
- **Custom Applications**: Your own events

**Event Targets:**
- **AWS Services**: Lambda, SQS, SNS, StepFunctions
- **API Destinations**: HTTP endpoints
- **Cross-Account**: Events to other AWS accounts

### EventBridge Rules

**Rule Components:**
- **Event Pattern**: Filter events based on content
- **Schedule**: Cron or rate expressions
- **Targets**: Where to send matching events
- **Input Transformation**: Modify event data

**Event Pattern Matching:**
- Filter on event source, detail type, and content
- Support for exact match, prefix, numeric ranges
- Complex logical expressions (AND, OR)
- Content-based routing

### EventBridge Best Practices

**Event Design:**
- Use consistent event schemas
- Include relevant context in events
- Avoid sensitive data in events
- Version your event schemas

**Rule Management:**
- Use descriptive rule names
- Implement proper error handling
- Monitor rule metrics and failures
- Use dead letter queues for failed events

## Systems Manager

### Parameter Store

**What is Parameter Store?**
- Centralized storage for configuration data
- Hierarchical parameter organization
- Encryption for sensitive data
- Integration with IAM for access control

**Parameter Types:**
- **String**: Plain text values
- **StringList**: Comma-separated values
- **SecureString**: Encrypted values

**Parameter Hierarchies:**
- Organize parameters by application, environment
- Path-based access control
- Batch operations on parameter trees
- Cross-region parameter replication

### Session Manager

**Secure Shell Access:**
- Browser-based SSH and RDP access
- No need for bastion hosts or open SSH ports
- Session logging and auditing
- Integration with IAM for access control

**Session Manager Benefits:**
- Centralized access logging
- No inbound firewall rules required
- Multi-factor authentication support
- Session recording for compliance

### Patch Manager

**Automated Patching:**
- Schedule patch installation
- Custom patch baselines
- Maintenance windows
- Compliance reporting

**Patch Groups:**
- Group instances for consistent patching
- Environment-specific patch schedules
- Staged rollout strategies
- Rollback capabilities

## Monitoring Best Practices

### Observability Strategy

**The Three Pillars:**
- **Metrics**: Quantitative measurements over time
- **Logs**: Detailed event records
- **Traces**: Request flow through distributed systems

**Monitoring Pyramid:**
- **Infrastructure**: CPU, memory, network, disk
- **Application**: Response time, error rate, throughput
- **Business**: User engagement, conversion rate, revenue

### Alerting Strategy

**Alert Design Principles:**
- **Actionable**: Alerts should require human action
- **Contextual**: Include relevant information for response
- **Tuned**: Avoid false positives and alert fatigue
- **Escalating**: Multiple notification levels

**On-Call Best Practices:**
- Clear escalation procedures
- Runbook documentation
- Post-incident reviews
- Rotation and burnout prevention

### Cost Optimization

**Monitoring Costs:**
- CloudWatch pricing based on metrics, logs, and alarms
- Custom metrics more expensive than default
- Log retention policies to control costs
- Use metric filters to reduce log processing

**Right-Sizing Monitoring:**
- Monitor what matters for business and operations
- Use sampling for high-volume applications
- Archive older data to cheaper storage
- Regular review and cleanup of unused resources

## Automation and Response

### Automated Remediation

**Common Automation Scenarios:**
- Auto-restart failed services
- Scale resources based on demand
- Security incident response
- Compliance violation fixes

**Implementation Patterns:**
- **CloudWatch Alarms**: Trigger immediate actions
- **EventBridge Rules**: React to AWS service events
- **Config Rules**: Automated compliance remediation
- **Systems Manager**: Operational automation

### Infrastructure as Code Monitoring

**CloudFormation Integration:**
- Monitor stack events and resources
- Detect drift from defined configurations
- Automated rollback on failures
- Template validation and testing

**Change Management:**
- Track all infrastructure changes
- Approval workflows for critical changes
- Testing in non-production environments
- Rollback procedures and documentation

## Summary

AWS monitoring and management tools provide comprehensive observability and automation capabilities:

- **CloudWatch**: Centralized monitoring for metrics, logs, and alarms
- **X-Ray**: Distributed tracing for microservices architectures
- **CloudTrail**: API activity logging and compliance auditing
- **Config**: Configuration management and compliance monitoring
- **EventBridge**: Event-driven automation and integration
- **Systems Manager**: Operational management and automation

Implementing effective monitoring requires strategic planning, proper instrumentation, and continuous optimization to maintain operational excellence while controlling costs.`,
    quiz: {
      questions: [
        {
          id: 'q1',
          question: 'Which CloudWatch alarm state indicates insufficient data to determine the metric status?',
          options: [
            'OK',
            'ALARM',
            'INSUFFICIENT_DATA',
            'UNKNOWN'
          ],
          correctAnswer: 2
        },
        {
          id: 'q2',
          question: 'What is the primary purpose of AWS X-Ray?',
          options: [
            'Log aggregation',
            'Distributed tracing',
            'Metric collection',
            'Security monitoring'
          ],
          correctAnswer: 1
        },
        {
          id: 'q3',
          question: 'Which service tracks AWS resource configuration changes over time?',
          options: [
            'CloudTrail',
            'CloudWatch',
            'Config',
            'Systems Manager'
          ],
          correctAnswer: 2
        },
        {
          id: 'q4',
          question: 'What is the main benefit of using EventBridge for event-driven architectures?',
          options: [
            'Lower latency',
            'Better security',
            'Decoupling of components',
            'Reduced costs'
          ],
          correctAnswer: 2
        }
      ]
    }
  },
  {
    id: 'aws-high-availability-dr',
    title: 'High Availability & Disaster Recovery',
    description: 'Design resilient architectures with fault tolerance and disaster recovery capabilities',
    duration: '75 minutes',
    difficulty: 'intermediate',
    order: 5,
    estimatedTime: '75 minutes',
    prerequisites: ['aws-compute-services-depth', 'aws-networking'],
    learningObjectives: [
      'Design fault-tolerant architectures across multiple AZs',
      'Implement disaster recovery strategies with appropriate RTO/RPO',
      'Configure automated backup and restore procedures',
      'Build self-healing infrastructure with Auto Scaling and health checks'
    ],
    content: `# High Availability & Disaster Recovery

This module focuses on designing and implementing resilient AWS architectures that can withstand failures and recover quickly from disasters while maintaining business continuity.

## High Availability Fundamentals

### Understanding Availability

**Availability Metrics:**
- **99.9% (8.76 hours downtime/year)**: Basic availability
- **99.95% (4.38 hours downtime/year)**: High availability
- **99.99% (52.56 minutes downtime/year)**: Very high availability
- **99.999% (5.26 minutes downtime/year)**: Extreme availability

**Factors Affecting Availability:**
- Infrastructure reliability
- Application design
- Operational procedures
- Human error prevention
- Change management processes

### AWS Global Infrastructure for HA

**Regions:**
- Geographically separated data centers
- Independent failure domains
- Compliance and data residency considerations
- Service availability varies by region

**Availability Zones:**
- Isolated locations within regions
- Independent power, cooling, networking
- Low-latency connections between AZs
- Foundation for high availability design

**Edge Locations:**
- CloudFront points of presence
- Improve performance and availability
- Reduce latency for global users
- DDoS protection and caching

## Fault-Tolerant Architecture Patterns

### Multi-AZ Deployments

**Web Tier High Availability:**
- Application Load Balancer across multiple AZs
- Auto Scaling Groups spanning AZs
- Health checks for automatic failover
- Session state management considerations

**Application Tier Resilience:**
- Stateless application design
- Auto Scaling for capacity and replacement
- Circuit breaker patterns
- Graceful degradation strategies

**Database High Availability:**
- RDS Multi-AZ for automatic failover
- DynamoDB multi-AZ by design
- ElastiCache cluster mode for Redis
- Cross-AZ read replicas

### Load Balancer Health Checks

**Health Check Configuration:**
- Target health evaluation criteria
- Health check intervals and timeouts
- Healthy and unhealthy thresholds
- Health check paths and ports

**Advanced Health Check Strategies:**
- Deep health checks vs shallow checks
- Dependency health verification
- Graceful shutdown procedures
- Health check endpoint design

### Auto Scaling for Resilience

**Instance Replacement:**
- Automatic unhealthy instance replacement
- Health check grace periods
- Launch template versioning
- Instance warm-up considerations

**Capacity Management:**
- Desired capacity maintenance
- Cross-AZ instance distribution
- Spot instance integration
- Reserved instance optimization

## Disaster Recovery Strategies

### RTO and RPO Planning

**Recovery Time Objective (RTO):**
- Maximum acceptable downtime
- Business impact analysis
- Technology and process constraints
- Cost vs availability trade-offs

**Recovery Point Objective (RPO):**
- Maximum acceptable data loss
- Backup frequency requirements
- Replication strategies
- Compliance considerations

### DR Strategy Types

**Backup and Restore:**
- **RTO**: Hours to days
- **RPO**: Hours
- **Cost**: Lowest
- **Approach**: Regular backups, restore when needed

**Pilot Light:**
- **RTO**: 10s of minutes to hours
- **RPO**: Minutes to hours
- **Cost**: Low to medium
- **Approach**: Core components always running

**Warm Standby:**
- **RTO**: Minutes to hours
- **RPO**: Minutes
- **Cost**: Medium
- **Approach**: Scaled-down fully functional environment

**Multi-Site Active/Active:**
- **RTO**: Real-time to minutes
- **RPO**: Near zero
- **Cost**: Highest
- **Approach**: Full production environment in multiple locations

### Implementing DR Strategies

**Backup and Restore Implementation:**
- Automated EBS snapshots
- RDS automated backups
- S3 cross-region replication
- Documentation and testing procedures

**Pilot Light Implementation:**
- Core infrastructure always running
- Database replication to DR region
- AMIs and launch templates ready
- Automated scaling procedures

**Warm Standby Implementation:**
- Reduced capacity environment running
- Real-time or near real-time replication
- Load balancer configuration for failover
- Monitoring and alerting setup

**Active/Active Implementation:**
- Full capacity in multiple regions
- Global load balancing with Route 53
- Data synchronization strategies
- Conflict resolution procedures

## Backup Strategies

### RDS Backup Options

**Automated Backups:**
- Point-in-time recovery capability
- 1-35 day retention period
- Backup window configuration
- Performance impact considerations

**Manual Snapshots:**
- User-initiated backups
- Indefinite retention
- Cross-region copying
- Encryption options

**Cross-Region Backup:**
- Automated cross-region snapshot copying
- Compliance and DR requirements
- Cost considerations
- Restore procedures

### EBS Snapshot Management

**Snapshot Lifecycle:**
- Automated snapshot creation
- Lifecycle policies for retention
- Cost optimization strategies
- Fast snapshot restore for critical volumes

**Snapshot Sharing:**
- Cross-account snapshot sharing
- Public snapshot considerations
- Encryption key management
- Access control policies

### S3 Backup and Versioning

**S3 Versioning:**
- Multiple versions of objects
- Accidental deletion protection
- MFA delete for additional security
- Lifecycle policies for old versions

**Cross-Region Replication:**
- Automatic replication to another region
- Same-region replication for compliance
- Replication rules and filters
- Monitoring replication status

**S3 Backup Tools:**
- AWS Backup service integration
- Third-party backup solutions
- Custom backup scripts
- Compliance and retention policies

## Route 53 for High Availability

### DNS Failover

**Health Check Types:**
- HTTP/HTTPS endpoint monitoring
- TCP connection monitoring
- Calculated health checks
- CloudWatch alarm-based checks

**Failover Routing:**
- Primary/secondary configuration
- Automatic failover based on health
- Multiple failover targets
- Global failover strategies

### Advanced Routing Policies

**Weighted Routing:**
- Distribute traffic across multiple endpoints
- Blue/green deployment support
- A/B testing capabilities
- Gradual traffic shifting

**Latency-Based Routing:**
- Route to lowest latency endpoint
- Global performance optimization
- Regional resource utilization
- User experience improvement

**Geolocation Routing:**
- Route based on user location
- Compliance with data sovereignty
- Content localization
- Regional service optimization

## Data Consistency and Synchronization

### Database Replication Strategies

**RDS Read Replicas:**
- Read scaling and availability
- Cross-region disaster recovery
- Promotion to standalone database
- Monitoring replication lag

**DynamoDB Global Tables:**
- Multi-region active-active replication
- Eventual consistency model
- Conflict resolution mechanisms
- Global application architecture

### Data Synchronization Challenges

**Consistency Models:**
- Strong consistency vs eventual consistency
- CAP theorem implications
- Business requirement analysis
- Technology choice impact

**Conflict Resolution:**
- Last-writer-wins strategies
- Application-level conflict resolution
- Timestamp-based resolution
- Custom conflict handlers

## Chaos Engineering

### Failure Testing

**Netflix Chaos Monkey:**
- Random instance termination
- Service resilience validation
- Automated failure injection
- Continuous resilience testing

**AWS Fault Injection Simulator:**
- Controlled fault injection
- Pre-defined failure scenarios
- Safety controls and stop conditions
- Experiment templates

### Testing Strategies

**Game Days:**
- Scheduled disaster recovery exercises
- Team coordination practice
- Process validation
- Documentation updates

**Continuous Testing:**
- Automated resilience testing
- CI/CD pipeline integration
- Gradual failure introduction
- Monitoring and alerting validation

## Monitoring and Alerting for HA/DR

### Critical Metrics

**Infrastructure Metrics:**
- Instance health and status
- Auto Scaling group metrics
- Load balancer health checks
- Network connectivity

**Application Metrics:**
- Response time and throughput
- Error rates and success rates
- Database performance
- Queue depths and processing times

**Business Metrics:**
- User experience indicators
- Transaction success rates
- Revenue impact metrics
- SLA compliance tracking

### Alerting Best Practices

**Alert Hierarchy:**
- Critical: Immediate response required
- Warning: Investigation needed
- Info: Awareness only
- Debug: Troubleshooting information

**Escalation Procedures:**
- Primary on-call notification
- Secondary escalation timing
- Management notification
- External vendor contacts

## Cost Optimization for HA/DR

### Right-Sizing DR Resources

**Capacity Planning:**
- Minimum viable capacity for DR
- Scaling capabilities and timing
- Resource reservation strategies
- Cost vs availability trade-offs

**Reserved Instance Strategy:**
- Regional vs AZ-specific reservations
- Convertible vs standard reservations
- DR region capacity planning
- Payment option optimization

### Cross-Region Cost Management

**Data Transfer Costs:**
- Cross-region replication charges
- CloudFront usage for global delivery
- Direct Connect for hybrid architectures
- VPN costs for site-to-site connectivity

**Storage Cost Optimization:**
- S3 storage class selection
- Lifecycle policies for backups
- Compression and deduplication
- Archive storage for long-term retention

## Compliance and Documentation

### Regulatory Requirements

**Data Protection:**
- GDPR compliance considerations
- Data residency requirements
- Encryption in transit and at rest
- Access logging and auditing

**Industry Standards:**
- SOC 2 compliance
- ISO 27001 requirements
- HIPAA considerations
- PCI DSS standards

### Documentation Requirements

**Runbooks:**
- Step-by-step recovery procedures
- Contact information and escalation
- Testing and validation steps
- Rollback procedures

**Architecture Documentation:**
- Network diagrams and configurations
- Data flow documentation
- Dependency mappings
- Recovery time estimates

## Testing and Validation

### DR Testing Types

**Tabletop Exercises:**
- Discussion-based scenario walkthroughs
- Process validation without actual failover
- Team coordination assessment
- Documentation review

**Simulation Testing:**
- Controlled failover exercises
- Partial system testing
- Network isolation testing
- Data recovery validation

**Full DR Testing:**
- Complete system failover
- End-to-end process validation
- Performance under DR conditions
- User acceptance testing

### Continuous Improvement

**Post-Incident Reviews:**
- Root cause analysis
- Process improvement identification
- Documentation updates
- Training needs assessment

**Metrics and KPIs:**
- RTO/RPO achievement tracking
- Test success rates
- Mean time to recovery
- Availability measurements

## Summary

High availability and disaster recovery require comprehensive planning and implementation:

- **Architecture Design**: Multi-AZ deployments and fault-tolerant patterns
- **DR Strategies**: Choose appropriate strategy based on RTO/RPO requirements
- **Backup Management**: Implement comprehensive backup and restore procedures
- **Testing**: Regular testing and validation of HA/DR capabilities
- **Monitoring**: Continuous monitoring and alerting for early problem detection
- **Documentation**: Maintain current procedures and contact information

Building resilient systems requires balancing availability requirements with cost constraints while ensuring business continuity and regulatory compliance.`,
    quiz: {
      questions: [
        {
          id: 'q1',
          question: 'What is the typical RTO for a "Warm Standby" disaster recovery strategy?',
          options: [
            'Real-time to minutes',
            'Minutes to hours',
            'Hours to days',
            '10s of minutes to hours'
          ],
          correctAnswer: 1
        },
        {
          id: 'q2',
          question: 'Which Route 53 routing policy routes traffic to the endpoint with the lowest latency?',
          options: [
            'Weighted routing',
            'Geolocation routing',
            'Latency-based routing',
            'Failover routing'
          ],
          correctAnswer: 2
        },
        {
          id: 'q3',
          question: 'What does RTO stand for in disaster recovery planning?',
          options: [
            'Recovery Time Objective',
            'Recovery Target Operations',
            'Restore Time Options',
            'Regional Technology Objectives'
          ],
          correctAnswer: 0
        },
        {
          id: 'q4',
          question: 'Which AWS service provides controlled fault injection for testing resilience?',
          options: [
            'CloudWatch',
            'Systems Manager',
            'Fault Injection Simulator',
            'Config'
          ],
          correctAnswer: 2
        }
      ]
    }
  }
];
